// recipe-inbox.js — parse forwarded recipe emails into the canonical Pronto
// recipe shape (matches docs/bulk-import-sample.json).
//
// Strategy:
//   1. Try JSON-LD extraction first — most cooking sites (NYT Cooking, Bon
//      Appétit, Serious Eats, Smitten Kitchen, etc.) embed schema.org/Recipe
//      structured data in the page HTML, which forwards verbatim through email
//      clients. Free, deterministic, no AI. Handles ~80% of useful forwards.
//   2. Fall back to a single Claude call against the email body for the rest
//      (plain-text recipes, blog posts without JSON-LD, friend forwards).
//
// Intentionally per the user's setup choice: parser does NOT auto-infer
// mood_tags / cuisine / course — those are left empty for the user to batch-edit
// after import. Tonight tab will surface untagged recipes once they're tagged.
//
// Photo-bearing forwards are handled in Phase 2 (vision pass on attachments).

const RECIPE_SYSTEM_PROMPT = `You extract recipes from forwarded emails for a personal recipe archive. The body may be a recipe pasted as text, an HTML email from a cooking site, or a friend's note describing how they made something.

Output: PURE JSON. No markdown fences, no prose outside JSON.

Rules:
1. Output a SINGLE recipe object, even if the email mentions multiple recipes — pick the most prominent one.
2. Preserve the source's step instructions with high fidelity. Keep ALL technique, timing, temperature, and sensory cues. Cleanup is OK (collapse extra whitespace, normalize "1/2"→"½", strip footnote markers, fix obvious typos). Do NOT compress, summarize, or paraphrase the steps. If the source has 5 steps with detailed instructions, preserve all 5 with their full detail.
3. Normalize times into integer minutes. If only "total" is given, leave prep/active/passive as 0 and put the value in total_min.
4. Ingredient amount should be a number when possible (e.g. 2, 0.5, 1.5). Use unit for the unit (cup, tbsp, oz, etc.). note captures qualifiers ("diced", "drained", "to taste").
5. If the source has labeled ingredient subsections (e.g. "FOR THE REFRIED BEANS", "FOR THE GLAZE", "FOR SERVING"), set the section field on each ingredient to the matching section title. If the recipe has no subsections, set section to an empty string on every ingredient.
6. source_type: "url" if the email links to a published recipe; "manual" otherwise.
7. source_ref: the URL of the published recipe if present, else null.
8. source_author: byline / chef name if visible, else null.
9. Leave cuisine, course, diet_tags, and mood_tags as empty arrays — the user assigns these post-import.
10. equipment: empty array unless the recipe explicitly calls out a specialty appliance ("dutch oven", "stand mixer", "instant pot"). Skip basic kitchen tools.
11. notes: capture any "Tip", "Note", "Make-ahead", or "Variation" block at the end of the recipe verbatim.
12. If the input is clearly NOT a recipe (newsletter, marketing email, unrelated forward), return {"title":"","_not_a_recipe":true,"parser_notes":"reason"}.

Output schema (every field required, use empty string / empty array / null where appropriate):
{
  "title": "string",
  "summary": "1-2 sentence description, or empty string",
  "source_type": "url|manual",
  "source_ref": "string or null",
  "source_author": "string or null",
  "cuisine": [],
  "course": [],
  "diet_tags": [],
  "mood_tags": [],
  "yield_amount": number,
  "yield_unit": "string (servings|bowls|loaves|etc.)",
  "prep_min": number,
  "active_min": number,
  "passive_min": number,
  "total_min": number,
  "ingredients": [
    {"item": "string", "amount": number or null, "unit": "string", "note": "string", "section": "string"}
  ],
  "steps": ["string"],
  "equipment": [],
  "notes": "string"
}`;

/**
 * Try to extract a schema.org/Recipe block from email HTML. Returns a normalized
 * recipe object on success, or null if no JSON-LD recipe data is found.
 *
 * Email clients sometimes mangle HTML, so we look for any <script type="application/ld+json">
 * blocks and inspect each. Handles single-Recipe blobs, @graph collections,
 * and arrays.
 */
export function extractRecipeJsonLd(html) {
  if (!html) return null;

  const blocks = [];
  const re = /<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    const raw = m[1].trim();
    try {
      blocks.push(JSON.parse(raw));
    } catch {
      // Some emails escape ampersands or break the JSON; try a quick rescue.
      try { blocks.push(JSON.parse(raw.replace(/&quot;/g, '"').replace(/&amp;/g, "&"))); }
      catch { /* skip — malformed */ }
    }
  }
  if (!blocks.length) return null;

  for (const block of blocks) {
    const recipe = findRecipeNode(block);
    if (recipe) return normalizeJsonLdRecipe(recipe);
  }
  return null;
}

// Walk a JSON-LD block (object, array, or @graph) and return the first
// node whose @type includes "Recipe".
function findRecipeNode(node) {
  if (!node) return null;
  if (Array.isArray(node)) {
    for (const n of node) {
      const found = findRecipeNode(n);
      if (found) return found;
    }
    return null;
  }
  if (typeof node !== "object") return null;
  if (Array.isArray(node["@graph"])) {
    return findRecipeNode(node["@graph"]);
  }
  const type = node["@type"];
  const types = Array.isArray(type) ? type : [type];
  if (types.some(t => typeof t === "string" && t.toLowerCase() === "recipe")) {
    return node;
  }
  return null;
}

// Map a schema.org/Recipe node to Pronto's bulk-import shape.
function normalizeJsonLdRecipe(r) {
  const ingredients = (r.recipeIngredient || []).map(line => parseIngredientLine(String(line)));

  // Instructions can be: array of strings, array of HowToStep objects,
  // a single string with newlines, or HowToSection wrapping HowToSteps.
  const steps = flattenInstructions(r.recipeInstructions);

  const totalMin = parseDurationMin(r.totalTime);
  const prepMin  = parseDurationMin(r.prepTime);
  const cookMin  = parseDurationMin(r.cookTime);

  const author = pickAuthor(r.author);
  const sourceUrl = r.url || (r.mainEntityOfPage && (r.mainEntityOfPage.url || r.mainEntityOfPage)) || null;

  return {
    title: cleanText(r.name) || "Untitled recipe",
    summary: cleanText(r.description) || "",
    source_type: sourceUrl ? "url" : "manual",
    source_ref: typeof sourceUrl === "string" ? sourceUrl : null,
    source_author: author,
    cuisine: [],
    course: [],
    diet_tags: [],
    mood_tags: [],
    yield_amount: parseYieldAmount(r.recipeYield),
    yield_unit: parseYieldUnit(r.recipeYield),
    prep_min: prepMin || 0,
    active_min: cookMin || 0,
    passive_min: 0,
    total_min: totalMin || (prepMin + cookMin) || 0,
    ingredients,
    steps,
    equipment: [],
    notes: "",
    _via: "json-ld",
  };
}

// Best-effort split of an ingredient line into amount/unit/item/note.
// "2 cups all-purpose flour, sifted" → {amount: 2, unit: "cups", item: "all-purpose flour", note: "sifted"}
function parseIngredientLine(line) {
  const original = line.trim();
  // Pull off a trailing ", note"
  let note = "";
  const noteMatch = original.match(/^(.*?),\s+([^,]+)$/);
  let head = original;
  if (noteMatch && /^[a-z]/.test(noteMatch[2])) {
    head = noteMatch[1];
    note = noteMatch[2].trim();
  }

  // Lead with a number, possibly fractional ("1 1/2", "1/2", "2", "0.5")
  const m = head.match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:[\s/]\d+\/\d+|\.\d+)?)\s+(.*)$/);
  if (!m) return { item: head, amount: null, unit: "", note, section: "" };

  const amount = parseAmountToken(m[1]);
  let rest = m[2];

  // Common units (single word): cup, tbsp, tsp, oz, lb, g, kg, ml, l, can, package, bunch, clove, slice, sprig, pinch
  const unitWords = ["cups?","tablespoons?","tbsps?","teaspoons?","tsps?","ounces?","oz","pounds?","lbs?","grams?","g","kilograms?","kg","milliliters?","ml","liters?","l","cans?","packages?","pkgs?","bunches?","cloves?","slices?","sprigs?","pinches?","dashes?","sticks?","heads?","stalks?","ribs?"];
  const unitRe = new RegExp(`^(${unitWords.join("|")})\\b\\.?\\s*(.*)$`, "i");
  const um = rest.match(unitRe);
  let unit = "";
  if (um) {
    unit = um[1].toLowerCase().replace(/s$/, "").replace(/\.$/, "");
    rest = um[2];
  }

  return { item: rest.trim(), amount, unit, note, section: "" };
}

function parseAmountToken(tok) {
  if (!tok) return null;
  const map = { "¼":0.25, "½":0.5, "¾":0.75, "⅓":1/3, "⅔":2/3, "⅛":0.125, "⅜":0.375, "⅝":0.625, "⅞":0.875 };
  if (map[tok]) return map[tok];
  // "1 1/2" or "1/2"
  const mixed = tok.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return +mixed[1] + (+mixed[2] / +mixed[3]);
  const frac = tok.match(/^(\d+)\/(\d+)$/);
  if (frac) return +frac[1] / +frac[2];
  const dec = parseFloat(tok);
  return isNaN(dec) ? null : dec;
}

function flattenInstructions(node) {
  if (!node) return [];
  if (typeof node === "string") {
    return node.split(/\n+/).map(s => s.trim()).filter(Boolean);
  }
  if (Array.isArray(node)) {
    const out = [];
    for (const n of node) out.push(...flattenInstructions(n));
    return out;
  }
  if (typeof node === "object") {
    if (node["@type"] === "HowToSection" && Array.isArray(node.itemListElement)) {
      return flattenInstructions(node.itemListElement);
    }
    if (node.text) return [cleanText(node.text)];
    if (node.name) return [cleanText(node.name)];
  }
  return [];
}

// "PT1H30M" → 90, "PT45M" → 45, "1 hour" → 60, "30 mins" → 30
function parseDurationMin(value) {
  if (!value) return 0;
  if (typeof value === "number") return Math.round(value);
  const s = String(value);
  const iso = s.match(/^PT(?:(\d+)H)?(?:(\d+)M)?/i);
  if (iso) return (+(iso[1] || 0)) * 60 + (+(iso[2] || 0));
  let total = 0;
  const h = s.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\b/i);
  const m = s.match(/(\d+)\s*(?:minutes?|mins?|m)\b/i);
  if (h) total += parseFloat(h[1]) * 60;
  if (m) total += parseInt(m[1], 10);
  return Math.round(total);
}

// recipeYield can be "4 servings", "Makes 4", 4, ["4 servings", "8 cookies"]
function parseYieldAmount(y) {
  if (!y) return 4;
  const v = Array.isArray(y) ? y[0] : y;
  if (typeof v === "number") return v;
  const m = String(v).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 4;
}

function parseYieldUnit(y) {
  if (!y) return "servings";
  const v = Array.isArray(y) ? y[0] : y;
  if (typeof v === "number") return "servings";
  const s = String(v).toLowerCase();
  if (s.includes("loaf") || s.includes("loaves")) return "loaves";
  if (s.includes("bowl")) return "bowls";
  if (s.includes("cookie")) return "cookies";
  if (s.includes("muffin")) return "muffins";
  if (s.includes("piece")) return "pieces";
  if (s.includes("portion")) return "portions";
  return "servings";
}

function pickAuthor(a) {
  if (!a) return null;
  if (typeof a === "string") return a;
  if (Array.isArray(a)) return a.map(x => (typeof x === "string" ? x : x?.name)).filter(Boolean).join(", ") || null;
  return a.name || null;
}

function cleanText(s) {
  if (!s) return "";
  return String(s).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

// Strip HTML to text. Conservative: drops scripts/styles, then tags.
export function stripHtmlToText(html) {
  if (!html) return "";
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Parse a recipe email body using Claude. Used as a fallback when JSON-LD
 * extraction fails (or as the primary path for plain-text emails).
 */
export async function parseRecipeFromEmail({ apiKey, text, html, subject = "" }) {
  if (!apiKey) throw new Error("Missing Anthropic API key");
  const body = text || stripHtmlToText(html || "");
  if (!body.trim()) throw new Error("Email has no text content");

  const userMsg = subject
    ? `Email subject: ${subject}\n\n--- BODY ---\n\n${body}`
    : body;

  const result = await callClaude({
    apiKey,
    systemPrompt: RECIPE_SYSTEM_PROMPT,
    userContent: userMsg,
  });

  if (result?._not_a_recipe) {
    throw new Error(`Not a recipe: ${result.parser_notes || "couldn't extract recipe"}`);
  }
  return { ...result, _via: "ai" };
}

/**
 * Vision-based parser. Pass an array of {base64, mediaType} images. Optional
 * text/html/subject context is included alongside the images so the model has
 * any caption text the user might have typed in the email body.
 */
export async function parseRecipeFromImages({ apiKey, images, text, html, subject = "" }) {
  if (!apiKey) throw new Error("Missing Anthropic API key");
  if (!images?.length) throw new Error("No images provided");

  const userContent = [];
  for (const img of images) {
    if (!img?.base64) continue;
    userContent.push({
      type: "image",
      source: {
        type: "base64",
        media_type: img.mediaType || "image/jpeg",
        data: img.base64,
      },
    });
  }
  if (userContent.length === 0) throw new Error("No usable images");

  const textBody = text || stripHtmlToText(html || "");
  const promptParts = ["Extract the recipe from these photos."];
  if (subject) promptParts.unshift(`Email subject: ${subject}`);
  if (textBody) promptParts.push(`Additional text from the email body:\n${textBody}`);
  userContent.push({ type: "text", text: promptParts.join("\n\n") });

  const result = await callClaude({
    apiKey,
    systemPrompt: RECIPE_SYSTEM_PROMPT,
    userContent,
  });

  if (result?._not_a_recipe) {
    throw new Error(`Not a recipe: ${result.parser_notes || "couldn't extract recipe"}`);
  }
  return { ...result, _via: "vision" };
}

/**
 * Top-level parser. Routing:
 *   - If images provided → vision (most reliable for screenshot forwards)
 *   - Else if HTML has JSON-LD recipe → free, deterministic extraction
 *   - Else fall back to text AI
 */
export async function parseRecipeInbox({ apiKey, text, html, subject, images }) {
  if (images?.length > 0) {
    return await parseRecipeFromImages({ apiKey, images, text, html, subject });
  }
  const fromJsonLd = extractRecipeJsonLd(html || "");
  if (fromJsonLd && fromJsonLd.title && fromJsonLd.ingredients?.length) {
    return fromJsonLd;
  }
  return await parseRecipeFromEmail({ apiKey, text, html, subject });
}

// ----- shared Claude call (mirrors receipts.js pattern) -----

async function callClaude({ apiKey, systemPrompt, userContent }) {
  const r = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 4096,
      system: systemPrompt,
      messages: [
        {
          role: "user",
          content: typeof userContent === "string"
            ? [{ type: "text", text: userContent }]
            : userContent,
        },
      ],
    }),
  });

  if (!r.ok) {
    const errText = await r.text();
    throw new Error(`Claude API error ${r.status}: ${errText.slice(0, 200)}`);
  }

  const data = await r.json();

  // Best-effort: emit a usage event so the AI usage logger picks it up.
  try {
    if (data.usage && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cucina-ai-usage", {
        detail: { kind: "recipe-inbox", model: "claude-sonnet-4-6", usage: data.usage },
      }));
    }
  } catch {}

  const text = data.content?.find(c => c.type === "text")?.text || "";
  const cleaned = text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    throw new Error(`Couldn't parse Claude response as JSON. First 200 chars: ${cleaned.slice(0, 200)}`);
  }
}
